import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { cn } from '../../lib/utils';

interface SkeletonProps {
  className?: string;
  count?: number;
  height?: number | string;
  width?: number | string;
  circle?: boolean;
  inline?: boolean;
  containerClassName?: string;
}

export function SkeletonLoader({
  className,
  count = 1,
  height,
  width,
  circle = false,
  inline = false,
  containerClassName,
}: SkeletonProps) {
  return (
    <Skeleton
      count={count}
      height={height}
      width={width}
      circle={circle}
      inline={inline}
      className={cn('animate-pulse', className)}
      containerClassName={containerClassName}
      baseColor="#f3f4f6"
      highlightColor="#e5e7eb"
    />
  );
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return <SkeletonLoader count={lines} />;
}

export function SkeletonCard() {
  return (
    <div className="rounded-lg border bg-card p-6">
      <SkeletonLoader height={20} width="60%" className="mb-2" />
      <SkeletonLoader height={32} width="40%" />
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="w-full">
      {/* Table Header */}
      <div className="border-b bg-gray-50 p-4">
        <div className="flex gap-4">
          <SkeletonLoader height={20} width="20%" />
          <SkeletonLoader height={20} width="30%" />
          <SkeletonLoader height={20} width="20%" />
          <SkeletonLoader height={20} width="15%" />
          <SkeletonLoader height={20} width="15%" />
        </div>
      </div>
      
      {/* Table Rows */}
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="border-b p-4">
          <div className="flex gap-4">
            <SkeletonLoader height={20} width="20%" />
            <SkeletonLoader height={20} width="30%" />
            <SkeletonLoader height={20} width="20%" />
            <SkeletonLoader height={20} width="15%" />
            <SkeletonLoader height={20} width="15%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonForm() {
  return (
    <div className="space-y-4">
      {/* Form fields */}
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index}>
          <SkeletonLoader height={16} width="30%" className="mb-2" />
          <SkeletonLoader height={40} width="100%" />
        </div>
      ))}
      
      {/* Submit button */}
      <SkeletonLoader height={40} width={120} />
    </div>
  );
}

export function SkeletonDashboardCard() {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <SkeletonLoader height={16} width="50%" className="mb-2" />
          <SkeletonLoader height={32} width="70%" />
        </div>
        <SkeletonLoader circle height={48} width={48} />
      </div>
    </div>
  );
}

// Theme provider for consistent skeleton styling
export function SkeletonProvider({ children }: { children: React.ReactNode }) {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#e5e7eb">
      {children}
    </SkeletonTheme>
  );
}